import { C } from '../palette';
import { PixelBuf } from '../pixel';

/**
 * Original enemies:
 *  - Grub   : a plodding vine grub, squashes flat when stomped
 *  - Snug   : an armoured beetle; stomping pops it into its shell, which can be kicked
 *  - Nipper : a snapping bloom that rises out of pipes
 */

/** Grub, 16x14. Two waddle frames. */
export function grubSprite(frame: number): PixelBuf {
  const p = new PixelBuf(16, 14);
  const body = C.grub;
  const dark = C.grubDark;
  // feet
  if (frame === 0) {
    p.rect(2, 12, 4, 2, C.grubFoot);
    p.rect(10, 12, 4, 2, C.grubFoot);
  } else {
    p.rect(1, 12, 5, 2, C.grubFoot);
    p.rect(11, 12, 4, 2, C.grubFoot);
  }
  // dome body with outline
  p.blob(2, 1, 12, 11, C.outline);
  p.blob(3, 2, 10, 9, body);
  p.rect(4, 3, 8, 2, dark);
  p.rect(3, 8, 10, 3, C.grubBelly);
  p.rect(4, 10, 8, 1, dark);
  // brow + eyes
  p.rect(4, 5, 3, 1, dark);
  p.rect(9, 5, 3, 1, dark);
  p.rect(4, 6, 3, 3, C.eye);
  p.rect(9, 6, 3, 3, C.eye);
  p.rect(5, 7, 2, 2, C.outline);
  p.rect(9, 7, 2, 2, C.outline);
  // mouth
  p.rect(6, 10, 4, 1, C.outline);
  return p;
}

/** Flattened grub, 16x8. */
export function grubSquashed(): PixelBuf {
  const p = new PixelBuf(16, 14);
  p.blob(1, 9, 14, 5, C.outline);
  p.blob(2, 10, 12, 3, C.grub);
  p.rect(3, 11, 10, 1, C.grubBelly);
  p.rect(3, 13, 3, 1, C.grubFoot);
  p.rect(10, 13, 3, 1, C.grubFoot);
  return p;
}

/** Snug walking, 16x16. */
export function snugSprite(frame: number): PixelBuf {
  const p = new PixelBuf(16, 16);
  // legs
  if (frame === 0) {
    p.rect(3, 14, 3, 2, C.snugDark);
    p.rect(10, 14, 3, 2, C.snugDark);
  } else {
    p.rect(2, 14, 3, 2, C.snugDark);
    p.rect(11, 14, 3, 2, C.snugDark);
  }
  // shell
  p.blob(1, 3, 14, 11, C.outline);
  p.blob(2, 4, 12, 9, C.shell);
  p.rect(3, 5, 10, 2, C.shellLight);
  p.rect(2, 11, 12, 2, C.shellRim);
  // shell plates
  p.rect(5, 7, 2, 3, C.shellDark);
  p.rect(9, 7, 2, 3, C.shellDark);
  // head poking out to the right
  p.blob(11, 0, 5, 5, C.outline);
  p.blob(12, 1, 4, 3, C.snug);
  p.px(14, 2, C.outline);
  p.px(13, 3, C.snugDark);
  return p;
}

/** Empty shell (idle or spinning), 16x12. */
export function shellSprite(spin: number): PixelBuf {
  const p = new PixelBuf(16, 12);
  p.blob(1, 1, 14, 11, C.outline);
  p.blob(2, 2, 12, 9, C.shell);
  p.rect(2, 8, 12, 2, C.shellRim);
  if (spin === 0) {
    p.rect(4, 4, 3, 3, C.shellDark);
    p.rect(9, 4, 3, 3, C.shellDark);
  } else if (spin === 1) {
    p.rect(3, 4, 3, 3, C.shellDark);
    p.rect(8, 3, 3, 3, C.shellDark);
    p.rect(11, 5, 2, 2, C.shellLight);
  } else {
    p.rect(5, 3, 3, 3, C.shellDark);
    p.rect(10, 4, 3, 3, C.shellDark);
    p.rect(3, 5, 2, 2, C.shellLight);
  }
  return p;
}

/** Nipper, 16x24; `open` selects the chomp frame. */
export function nipperSprite(open: boolean): PixelBuf {
  const p = new PixelBuf(16, 24);
  // stem
  p.rect(6, 10, 4, 14, C.stemDark);
  p.rect(7, 10, 2, 14, C.stem);
  // leaves
  p.rect(2, 16, 4, 2, C.stem);
  p.rect(10, 19, 4, 2, C.stem);
  // head
  const spread = open ? 3 : 1;
  p.blob(2, 6 - spread, 12, 6, C.outline);
  p.blob(3, 7 - spread, 10, 4, C.nipper);
  p.rect(4, 8 - spread, 8, 1, C.nipperLight);
  p.blob(2, 11, 12, 5, C.outline);
  p.blob(3, 11, 10, 3, C.nipperDark);
  // teeth
  for (let i = 0; i < 4; i++) {
    p.rect(4 + i * 3, 11 - spread + 1, 2, spread, C.white);
    p.rect(4 + i * 3, 11, 2, 2, C.white);
  }
  // spots
  p.px(5, 9 - spread, C.white);
  p.px(10, 9 - spread, C.white);
  return p;
}
